import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userHasOrganization } from '../pages/setPasswordFlow';
import Spinner from 'react-bootstrap/Spinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isAuthenticated, loading, mustChangePassword, pendingMfa } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with the current location as the return URL
    return <Navigate to={pendingMfa ? "/mfa" : "/login"} state={{ from: location }} replace />;
  }

  // If the user must change their password, lock them to /set-password
  if (mustChangePassword && location.pathname !== '/set-password') {
    return <Navigate to="/set-password" replace />;
  }

  const isSuperAdmin = String(user?.role || '').trim().toLowerCase() === 'superadmin';
  const needsOrganizationSetup = Boolean(user) && !isSuperAdmin && !userHasOrganization(user);
  if (needsOrganizationSetup && location.pathname !== '/profile-setup' && location.pathname !== '/set-password') {
    return <Navigate to="/profile-setup" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
